import type { ChangeEvent, Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useState } from "react";

import { toast } from "@jti/core-components/components/Toaster";
import { buildErrorDictionary, isApiErrorWithErrorCode, translateBackendErrorCode } from "@jti/fe-common";
import { getUser, LoyaltyApi } from "@jti/rest-api";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import type { ArticleDataPartial } from "../../ArticleRestrictedContent/ArticleRestrictedContent";
import type { QuizAnswerState, QuizContentConfig, QuizQuestionConfig } from "../types";
import { QUIZ_ANSWER_STATE, replacePointsPlaceholder } from "../types";

const QUIZ_ERROR_CODES = {
  alreadyCompletedMessage: "loyalty.earnings::quiz_has_been_already_completed",
  notAvailableMessage: "loyalty.earnings::quiz_not_available_for_consumer",
} as const;

type UseQuizContentParams = {
  config: QuizContentConfig;
  articleId?: string;
  points: number | null;
  isOpened: boolean;
  onClose: () => void;
  setArticleData: Dispatch<SetStateAction<ArticleDataPartial>>;
};

const INITIAL_STATE = {
  currentQuestionIndex: 0,
  selectedAnswer: "",
  answerState: QUIZ_ANSWER_STATE.idle,
  isCompleted: false,
  isSubmitting: false,
  isShowingExitConfirmation: false,
};

export const useQuizContent = ({
  config,
  articleId,
  points,
  isOpened,
  onClose,
  setArticleData,
}: UseQuizContentParams) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(INITIAL_STATE.currentQuestionIndex);
  const [selectedAnswer, setSelectedAnswer] = useState(INITIAL_STATE.selectedAnswer);
  const [answerState, setAnswerState] = useState<QuizAnswerState>(INITIAL_STATE.answerState);
  const [isCompleted, setIsCompleted] = useState(INITIAL_STATE.isCompleted);
  const [isSubmitting, setIsSubmitting] = useState(INITIAL_STATE.isSubmitting);
  const [isShowingExitConfirmation, setIsShowingExitConfirmation] = useState(INITIAL_STATE.isShowingExitConfirmation);

  const resetState = useCallback(() => {
    setCurrentQuestionIndex(INITIAL_STATE.currentQuestionIndex);
    setSelectedAnswer(INITIAL_STATE.selectedAnswer);
    setAnswerState(INITIAL_STATE.answerState);
    setIsCompleted(INITIAL_STATE.isCompleted);
    setIsSubmitting(INITIAL_STATE.isSubmitting);
    setIsShowingExitConfirmation(INITIAL_STATE.isShowingExitConfirmation);
  }, []);

  useEffect(() => {
    if (isOpened) {
      resetState();
      dispatchTrackingEvent(TrackingEvents.TrackingQuizStart);
    }
  }, [isOpened, resetState]);

  const currentQuestion: QuizQuestionConfig = config.questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === config.questions.length - 1;
  const isInProgress = !isCompleted && (currentQuestionIndex > 0 || answerState !== QUIZ_ANSWER_STATE.idle);

  const handleAnswerSelect = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const answerIndex = event.target.value;
      setSelectedAnswer(answerIndex);

      const selectedOption = currentQuestion.options[Number.parseInt(answerIndex, 10)];
      setAnswerState(selectedOption?.isCorrect ? QUIZ_ANSWER_STATE.correct : QUIZ_ANSWER_STATE.incorrect);
    },
    [currentQuestion]
  );

  const handleNext = useCallback(async () => {
    if (answerState !== QUIZ_ANSWER_STATE.correct) return;

    if (!isLastQuestion) {
      setCurrentQuestionIndex((prev) => prev + 1);
      setSelectedAnswer("");
      setAnswerState(QUIZ_ANSWER_STATE.idle);
      return;
    }

    try {
      setIsSubmitting(true);
      const { uuid } = getUser();
      if (!uuid || !articleId) return;
      await LoyaltyApi.init().completeQuizAction(articleId, uuid);
      toast.success(replacePointsPlaceholder(config.successToastMessage, points ?? 0));
      setArticleData((prev) => ({ ...prev, is_completed: true }));
      setIsCompleted(true);
      dispatchTrackingEvent(TrackingEvents.TrackingQuizEnd, { points_earned: points ?? 0 });
    } catch (error) {
      toast.error(
        translateBackendErrorCode(
          isApiErrorWithErrorCode(error) ? error.error_code : undefined,
          config.errorToastMessage,
          buildErrorDictionary(
            {
              alreadyCompletedMessage: config.alreadyCompletedMessage,
              notAvailableMessage: config.notAvailableMessage,
            },
            QUIZ_ERROR_CODES
          )
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    answerState,
    isLastQuestion,
    articleId,
    points,
    setArticleData,
    config.successToastMessage,
    config.errorToastMessage,
    config.alreadyCompletedMessage,
    config.notAvailableMessage,
  ]);

  const handleDropQuiz = useCallback(() => {
    onClose();
    resetState();
  }, [onClose, resetState]);

  const handleCloseRequest = useCallback(() => {
    if (isShowingExitConfirmation) {
      handleDropQuiz();
      return;
    }
    if (isInProgress) {
      setIsShowingExitConfirmation(true);
      return;
    }
    onClose();
  }, [isShowingExitConfirmation, isInProgress, handleDropQuiz, onClose]);

  const handleStay = useCallback(() => {
    setIsShowingExitConfirmation(false);
  }, []);

  return {
    currentQuestion,
    currentQuestionIndex,
    selectedAnswer,
    answerState,
    isCompleted,
    isSubmitting,
    isShowingExitConfirmation,
    handleAnswerSelect,
    handleNext,
    handleCloseRequest,
    handleDropQuiz,
    handleStay,
  };
};
